import {
  collection,
  doc,
  getDoc,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { logActivity } from '@/lib/activityLog';

export interface BulkStockItem {
  productId: string;
  stock: number;
}

export interface BulkStockUpdateOptions {
  storeId?: string;
  actorId?: string | null;
  actorName?: string | null;
}

export interface BulkStockUpdateResult {
  updatedCount: number;
  results: { productId: string; oldStock: number; newStock: number }[];
}

/**
 * Validates and applies bulk stock updates in a single Firestore batch.
 *
 * Requirements:
 * - Product IDs must exist.
 * - Stock values must be non-negative integers.
 * - Updates datahub_inventory quantity and recalculates lowStock.
 * - Creates stock_movements ledger entries if storeId is provided.
 * - Emits activity logs for stock increases and decreases.
 */
export async function bulkStockUpdate(
  items: BulkStockItem[],
  options?: BulkStockUpdateOptions,
): Promise<BulkStockUpdateResult> {
  if (!Array.isArray(items) || items.length === 0) {
    throw new Error('No items provided for stock update.');
  }

  // 1. Client-side input validation
  for (const it of items) {
    if (!it.productId || typeof it.productId !== 'string') {
      throw new Error(`Invalid productId: ${it.productId}`);
    }
    if (
      typeof it.stock !== 'number' ||
      !Number.isFinite(it.stock) ||
      it.stock < 0
    ) {
      throw new Error(
        `Invalid stock value for product ${it.productId}. Stock must be a non-negative number.`,
      );
    }
  }

  // 2. Fetch current docs to verify existence and calculate deltas
  const docSnaps = await Promise.all(
    items.map((it) => getDoc(doc(db, 'datahub_inventory', it.productId))),
  );

  for (let i = 0; i < docSnaps.length; i++) {
    if (!docSnaps[i].exists()) {
      throw new Error(`Product with ID "${items[i].productId}" not found.`);
    }
  }

  // 3. Prepare atomic batch
  const batch = writeBatch(db);
  const results: { productId: string; oldStock: number; newStock: number }[] = [];

  for (let i = 0; i < items.length; i++) {
    const it = items[i];
    const snap = docSnaps[i];
    const data = snap.data() || {};
    const oldStock = Number(data.quantity) || 0;
    const newStock = Math.floor(it.stock);
    const lowStockThreshold = Number(data.lowStockThreshold) || 5;
    const itemName = (data.itemName as string) || 'Product';

    if (oldStock === newStock) {
      continue;
    }

    const itemRef = doc(db, 'datahub_inventory', it.productId);
    batch.update(itemRef, {
      quantity: newStock,
      lowStock: newStock <= lowStockThreshold,
      updatedAt: serverTimestamp(),
    });

    // If storeId is provided, record stock movement in ledger
    if (options?.storeId) {
      const movementRef = doc(collection(db, 'stock_movements'));
      const delta = newStock - oldStock;
      batch.set(movementRef, {
        itemId: it.productId,
        storeId: options.storeId,
        type: delta > 0 ? 'RESTOCK_IN' : 'MANUAL_OUT',
        quantity: Math.abs(delta),
        note: `Bulk stock adjustment from ${oldStock} to ${newStock}`,
        actorId: options?.actorId ?? null,
        applied: false,
        createdAt: serverTimestamp(),
      });
    }

    results.push({ productId: it.productId, oldStock, newStock });

    // Activity log entry for this adjustment
    const delta = newStock - oldStock;
    const isIncrease = delta > 0;
    logActivity({
      action: isIncrease ? 'stock_increased' : 'stock_decreased',
      title: isIncrease ? 'Stock Increased' : 'Stock Decreased',
      description: `${itemName}: ${oldStock} → ${newStock} (${isIncrease ? '+' : ''}${delta})`,
      actorId: options?.actorId ?? null,
      actorName: options?.actorName ?? null,
      entityType: 'stock',
      entityId: it.productId,
      metadata: {
        productId: it.productId,
        productName: itemName,
        oldStock,
        newStock,
        delta,
        storeId: options?.storeId ?? null,
      },
    });
  }

  // 4. Commit atomic batch
  if (results.length > 0) {
    await batch.commit();
  }

  return { updatedCount: results.length, results };
}
